import { formatIsoDateForLocale } from '@ascleo/types/date-time-validity'

/**
 * Raw appointment slot returned by the bridge before local display grouping.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const slot: TimeSlot = {
 *   id: 'slot-1',
 *   calendarId: 'calendar-1',
 *   start: '2026-06-15T08:00:00.000Z',
 *   end: '2026-06-15T08:30:00.000Z',
 *   appointmentTypeId: 'checkup',
 * }
 * ```
 */
export interface TimeSlot {
  id: string
  calendarId: string
  start: string
  end: string
  appointmentTypeId: string
}

/**
 * Time-of-day bucket used for filtering and displaying available slots.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const preferredPeriod: TimePeriod = 'morning'
 * ```
 */
export type TimePeriod = 'morning' | 'midday' | 'afternoon' | 'evening'

/**
 * Slot enriched with a localized time label and period bucket.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const slot: SlotWithTime = {
 *   id: 'slot-1',
 *   calendarId: 'calendar-1',
 *   time: '08:00',
 *   start: '2026-06-15T08:00:00.000Z',
 *   end: '2026-06-15T08:30:00.000Z',
 *   period: 'morning',
 * }
 * ```
 */
export interface SlotWithTime {
  id: string
  calendarId: string
  time: string
  start: string
  end: string
  dateFormatted?: string
  period: TimePeriod
}

/**
 * Calendar-cell view model for the month picker.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const day: CalendarDay = {
 *   date: '2026-06-15',
 *   dayNum: 15,
 *   isPast: false,
 *   isToday: true,
 *   slotCount: 2,
 *   periodsAvailable: new Set(['morning']),
 * }
 * ```
 */
export interface CalendarDay {
  date: string
  dayNum: number
  isPast: boolean
  isToday: boolean
  slotCount: number
  periodsAvailable: ReadonlySet<TimePeriod>
}

/**
 * Quick-pick slot with the local ISO date and formatted date label attached.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const slot: QuickSlot = {
 *   id: 'slot-1',
 *   time: '08:00',
 *   start: '2026-06-15T08:00:00.000Z',
 *   end: '2026-06-15T08:30:00.000Z',
 *   period: 'morning',
 *   date: '2026-06-15',
 *   dateFormatted: 'Mo., 15.06.',
 * }
 * ```
 */
export type QuickSlot = SlotWithTime & {
  date: string
  dateFormatted: string
}

/**
 * Minimum slot data required to commit a scheduler selection.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const selected: SelectableSlot = {
 *   id: 'slot-1',
 *   calendarId: 'calendar-1',
 *   start: '2026-06-15T08:00:00.000Z',
 *   end: '2026-06-15T08:30:00.000Z',
 * }
 * ```
 */
export type SelectableSlot = Pick<SlotWithTime, 'id' | 'calendarId' | 'start' | 'end'>

/**
 * Reason the slot list cannot be displayed.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const reason: SlotLoadErrorReason = 'fetchFailed'
 * ```
 */
export type SlotLoadErrorReason = 'missingAppointmentType' | 'fetchFailed'

/**
 * Async load state for scheduler slot retrieval.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const state: SlotLoadState = { status: 'loading', slots: [] }
 * ```
 */
export type SlotLoadState =
  | { status: 'loading'; slots: TimeSlot[] }
  | { status: 'ready'; slots: TimeSlot[] }
  | { status: 'error'; slots: TimeSlot[]; reason: SlotLoadErrorReason }

/**
 * Reducer action for scheduler slot retrieval state.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const action: SlotLoadAction = { type: 'loading-started' }
 * ```
 */
export type SlotLoadAction =
  | { type: 'loading-started' }
  | { type: 'slots-loaded'; slots: TimeSlot[] }
  | { type: 'missing-appointment-type' }
  | { type: 'slots-load-failed' }

/**
 * Translation keys used for slot load failures.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const messageKey = SLOT_LOAD_ERROR_MESSAGES.fetchFailed
 * ```
 */
export const SLOT_LOAD_ERROR_MESSAGES: Record<SlotLoadErrorReason, string> = {
  missingAppointmentType: 'self-service.scheduler.slot-selection.no-appointment-type',
  fetchFailed: 'self-service.scheduler.slot-selection.error'
}

/**
 * Initial reducer state for scheduler slot loading.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const state = initialSlotLoadState
 * ```
 */
export const initialSlotLoadState: SlotLoadState = { status: 'loading', slots: [] }

/**
 * Returns the load-state transition for scheduler slot retrieval.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const next = slotLoadReducer(initialSlotLoadState, { type: 'slots-loaded', slots: [] })
 * ```
 */
export function slotLoadReducer(
  state: SlotLoadState,
  action: SlotLoadAction,
): SlotLoadState {
  switch (action.type) {
    case 'loading-started':
      return { status: 'loading', slots: state.slots }
    case 'slots-loaded':
      return { status: 'ready', slots: action.slots }
    case 'missing-appointment-type':
      return { status: 'error', slots: [], reason: 'missingAppointmentType' }
    case 'slots-load-failed':
      return { status: 'error', slots: state.slots, reason: 'fetchFailed' }
  }
}

/**
 * Maps a local hour to the scheduler's time-of-day period.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const period = getTimePeriod(9)
 * ```
 */
export function getTimePeriod(hour: number): TimePeriod {
  if (hour >= 8 && hour < 11) return 'morning'
  if (hour >= 11 && hour < 14) return 'midday'
  if (hour >= 14 && hour < 17) return 'afternoon'
  return 'evening'
}

/**
 * Formats a compact localized date label for scheduler slot chips.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const label = shortDateLabel('2026-06-15', 'de')
 * ```
 */
export function shortDateLabel(date: string, locale: string): string {
  return formatIsoDateForLocale(date, locale, {
    day: '2-digit',
    month: '2-digit',
    weekday: 'short'
  }).replace(/\s+/g, ' ')
}
